import { useSyncExternalStore } from 'react';
export type StreamStatus = 'idle' | 'connecting' | 'open' | 'retrying' | 'offline';
export interface StreamHealth {
  status: StreamStatus;
  receivedFrame: boolean;
  lastError: string | null;
  everConnected: boolean;
}
const INITIAL: StreamHealth = {
  status: 'idle',
  receivedFrame: false,
  lastError: null,
  everConnected: false,
};
let state: StreamHealth = INITIAL;
const listeners = new Set<(next: StreamHealth) => void>();
function emit(patch: Partial<StreamHealth>): void {
  state = { ...state, ...patch };
  for (const listener of listeners) listener(state);
}
export function getStreamHealth(): StreamHealth {
  return state;
}
export function subscribeStreamHealth(listener: (next: StreamHealth) => void): () => void {
  listeners.add(listener);
  listener(state);
  return () => {
    listeners.delete(listener);
  };
}
export function setBrowserOnline(online: boolean): void {
  emit(online ? { status: state.everConnected ? 'retrying' : 'idle' } : { status: 'offline' });
}
export function setStatus(status: StreamStatus): void {
  emit({
    status,
    ...(status === 'open' ? { everConnected: true, lastError: null } : {}),
  });
}
export function noteFrame(): void {
  if (!state.receivedFrame) emit({ receivedFrame: true });
}
export function noteError(message: string): void {
  emit({ lastError: message, status: 'retrying' });
}
export function resetStreamHealth(): void {
  state = INITIAL;
  for (const listener of listeners) listener(state);
}
export function useStreamHealth(): StreamHealth {
  return useSyncExternalStore(subscribeStreamHealth, getStreamHealth, getStreamHealth);
}
export function useStreamStatus(): StreamStatus {
  return useStreamHealth().status;
}
